<template>
  <div class="step-panel" data-testid="payroll-step-cash-recebimentos">
    <!-- Step Header -->
    <div class="step-header">
      <h2>{{ t('Adiantamentos de Folha') }}</h2>
    </div>

    <!-- Helper Banner -->
    <div class="helper-banner">
      <i class="pi pi-info-circle"></i>
      <div class="helper-content">
        <strong>{{ t('Adiantamentos (Vales)') }}</strong>
        <ul>
          <li>{{ t('Adiantamentos são valores pagos antecipadamente aos colaboradores') }}</li>
          <li>{{ t('Esses valores serão descontados do pagamento final do período') }}</li>
          <li>{{ t('Você pode adicionar, remover ou revisar os adiantamentos antes de confirmar') }}</li>
        </ul>
      </div>
    </div>

    <!-- Warning Banner -->
    <div class="warning-banner">
      <i class="pi pi-exclamation-triangle"></i>
      <div class="warning-content">
        <strong>{{ t('Atenção') }}</strong>
        <p>{{ t('Os adiantamentos serão deduzidos do valor líquido a pagar ao colaborador. Certifique-se de que os valores estão corretos.') }}</p>
      </div>
    </div>

    <!-- Important Notes -->
    <div class="important-notes">
      <div class="notes-header">
        <i class="pi pi-info-circle"></i>
        <h3>{{ t('Próximos Passos') }}</h3>
      </div>
      <ul class="notes-list">
        <li>
          <i class="pi pi-check-circle"></i>
          <span>{{ t('Após concluir os adiantamentos, prossiga para os reembolsos se houver solicitações pendentes') }}</span>
        </li>
        <li>
          <i class="pi pi-check-circle"></i>
          <span>{{ t('Caso contrário, prossiga diretamente para as deduções') }}</span>
        </li>
      </ul>
    </div>

    <p class="step-description">
      {{ t('Gerencie os recebimentos em dinheiro marcados como adiantamentos para este período') }}
    </p>

    <inline-message severity="info" :closable="false" class="info-message">
      <i class="pi pi-info-circle"></i>
      {{ t('Recebimentos em dinheiro marcados como adiantamento serão descontados do valor a pagar ao colaborador. Uma despesa com status "baixado" será criada para cada adiantamento.') }}
    </inline-message>

    <inline-message v-if="parseInt(String(periodStatus ?? 0)) === 4" severity="warn" :closable="false" class="reconfirm-warning-message">
      <template #icon>
        <i class="pi pi-exclamation-triangle" style="font-size: 1.25rem"></i>
      </template>
      <div class="warning-content">
        <h4 class="warning-title">{{ t('Período Já Confirmado - Atenção ao Reconfirmar') }}</h4>
        <p class="warning-text">
          {{ t('Este período já foi confirmado. Você pode adicionar ou remover adiantamentos e clicar em "Confirmar" novamente.') }}
        </p>
        <div class="warning-highlight">
          <strong>{{ t('IMPORTANTE:') }}</strong>
          {{ t('Reconfirmar irá SUBSTITUIR as despesas de salário pendentes anteriores.') }}
        </div>
      </div>
    </inline-message>

    <div v-if="isLoading" class="loading-container">
      <spinner />
    </div>

    <div v-else class="advances-container">
      <!-- Conciliation Status Card -->
      <div class="conciliation-card">
        <div class="card-body">
          <!-- Summary Grid -->
          <div class="summary-grid">
            <div class="summary-item">
              <div class="item-label">{{ t('Status') }}</div>
              <div class="item-value">
                <span class="status-indicator" :class="statusClass"></span>
                {{ t(statusText) }}
              </div>
            </div>

            <div class="summary-item">
              <div class="item-label">{{ t('Adiantamentos Selecionados') }}</div>
              <div class="item-value">{{ advances.length }}</div>
            </div>

            <div class="summary-item highlight">
              <div class="item-label">{{ t('Valor Total') }}</div>
              <div class="item-value">{{ n(parseFloat(String(totalAdvances ?? 0)) || 0, 'currency') }}</div>
            </div>
          </div>

          <!-- Action Button -->
          <div class="action-section">
            <Button
              :label="buttonLabel"
              :icon="buttonIcon"
              @click="openConciliationWizard"
              :severity="buttonSeverity"
              size="large"
              class="action-button"
              data-testid="payroll-step-cash-recebimentos-manage-btn"
            />
          </div>

          <!-- Info Messages based on status -->
          <div v-if="conciliationStatus === 'pending'" class="info-message-inline pending">
            <i class="pi pi-info-circle"></i>
            <span>{{ t('Clique em "Gerenciar Adiantamentos" para começar a selecionar recebimentos em dinheiro como adiantamentos.') }}</span>
          </div>

          <div v-else-if="conciliationStatus === 'in_progress'" class="info-message-inline in-progress">
            <i class="pi pi-sync"></i>
            <span>{{ t('Adiantamentos em progresso. Você pode continuar adicionando ou removendo adiantamentos.') }}</span>
          </div>

          <div v-else-if="conciliationStatus === 'completed'" class="info-message-inline completed">
            <i class="pi pi-check-circle"></i>
            <span>{{ t('Adiantamentos configurados. Você pode editar a seleção clicando no botão acima.') }}</span>
          </div>
        </div>
      </div>

      <!-- Summary Panel (if there are advances) -->
      <CashRecebimentosSummary
        v-if="advances.length > 0"
        :summary="summaryByUser"
        :total="totalAdvances"
        :usersMap="usersMap"
      />
    </div>

    <!-- CashRecebimentosConciliationWizard -->
    <CashRecebimentosConciliationWizard
      v-if="periodId && franchiseId"
      :visible="showConciliationWizard"
      :period-id="periodId"
      :franchise-id="franchiseId"
      :users-map="usersMap"
      @update:visible="showConciliationWizard = $event"
      @completed="onConciliationCompleted"
      @closed="onWizardClosed"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePayrollAdvances } from '@/composables/usePayrollAdvances';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import CashRecebimentosSummary from './CashRecebimentosSummary.vue';
import CashRecebimentosConciliationWizard from './CashRecebimentosConciliationWizard.vue';

type Id = number | string;

interface PaymentByUser {
  user_id?: Id;
  user_name?: string;
}

interface UserMapEntry {
  name?: string;
  user_name?: string;
  [key: string]: unknown;
}

interface AdvanceRow {
  [key: string]: unknown;
}

interface SummaryByUserRow {
  user_id?: Id;
  advance_count?: number | string;
  total_advances?: number | string;
  [key: string]: unknown;
}

interface AdvancesByPeriodResponse {
  advances?: AdvanceRow[];
  summary_by_user?: SummaryByUserRow[];
  total_advances?: number | string;
}

interface WizardMachine {
  context: { value: {
    period?: {
      id?: Id;
      franchise_id?: Id;
      status?: number | string;
    };
    periodFormData?: {
      franchise_id?: Id | null;
    };
    paymentByUser?: PaymentByUser[];
  } };
}

// =============================================================================
// INJECT MACHINE - Single Source of Truth
// =============================================================================

const machine = inject<WizardMachine>('wizardMachine')!;

// =============================================================================
// REACTIVE DATA FROM MACHINE
// =============================================================================

/**
 * Get period ID from machine context (converted to Number for prop type safety)
 */
const periodId = computed<number | null>(() => {
  const id = machine.context.value.period?.id;
  return id ? Number(id) : null;
});

/**
 * Get franchise ID from machine context
 */
const franchiseId = computed<number | null>(() => {
  return Number(machine.context.value.period?.franchise_id) ||
         Number(machine.context.value.periodFormData?.franchise_id) ||
         null;
});

/**
 * Get period status from machine context
 */
const periodStatus = computed<number | string>(() => {
  return machine.context.value.period?.status || 0;
});

/**
 * Build users map from payment by user data
 */
const usersMap = computed<Record<string, UserMapEntry>>(() => {
  const users = machine.context.value.paymentByUser || [];
  const map: Record<string, UserMapEntry> = {};
  users.forEach((u) => {
    if (u.user_id) {
      map[String(u.user_id)] = { name: u.user_name || `User ${u.user_id}` };
    }
  });
  return map;
});

const { t, n } = useI18n();
const { listAdvancesByPeriod } = usePayrollAdvances();

const isLoading = ref<boolean>(false);
const showConciliationWizard = ref<boolean>(false);

// Data
const advances = ref<AdvanceRow[]>([]);
const summaryByUser = ref<SummaryByUserRow[]>([]);
// total_advances may arrive as a stringified decimal from the API
// (PHP returns DECIMAL columns as strings). Keep the union to preserve
// the runtime-equivalent parseFloat() behavior in the template.
const totalAdvances = ref<number | string>(0);

// Computed: conciliation status based on advances count
type ConciliationStatus = 'pending' | 'in_progress' | 'completed';

const conciliationStatus = computed<ConciliationStatus>(() => {
  if (advances.value.length === 0) {
    return 'pending';
  }
  // Consider it "in_progress" if period is not confirmed yet
  if (parseInt(String(periodStatus.value ?? 0), 10) < 4) {
    return 'in_progress';
  }
  return 'completed';
});

// Computed: status class for indicator
const statusClass = computed<string>(() => {
  switch (conciliationStatus.value) {
    case 'pending':
      return 'pending';
    case 'in_progress':
      return 'in-progress';
    case 'completed':
      return 'completed';
    default:
      return 'pending';
  }
});

// Computed: status text
const statusText = computed<string>(() => {
  switch (conciliationStatus.value) {
    case 'pending':
      return 'Pendente';
    case 'in_progress':
      return 'Em Progresso';
    case 'completed':
      return 'Concluído';
    default:
      return 'Pendente';
  }
});

// Computed: button label based on status
const buttonLabel = computed<string>(() => {
  switch (conciliationStatus.value) {
    case 'pending':
      return t('Gerenciar Adiantamentos');
    case 'in_progress':
      return t('Continuar Seleção');
    case 'completed':
      return t('Editar Adiantamentos');
    default:
      return t('Gerenciar Adiantamentos');
  }
});

// Computed: button icon based on status
const buttonIcon = computed<string>(() => {
  switch (conciliationStatus.value) {
    case 'pending':
      return 'pi pi-wallet';
    case 'in_progress':
      return 'pi pi-sync';
    case 'completed':
      return 'pi pi-pencil';
    default:
      return 'pi pi-wallet';
  }
});

// Computed: button severity based on status
const buttonSeverity = computed<'primary' | 'warning' | 'success'>(() => {
  switch (conciliationStatus.value) {
    case 'pending':
      return 'primary';
    case 'in_progress':
      return 'warning';
    case 'completed':
      return 'success';
    default:
      return 'primary';
  }
});

// Load advances data
const loadData = async (): Promise<void> => {
  if (!periodId.value) {
    log('StepCashRecebimentos', 'loadData', 'No period ID available yet');
    return;
  }

  isLoading.value = true;
  try {
    log('StepCashRecebimentos', 'loadData', 'Loading data', { periodId: periodId.value });

    const data = await listAdvancesByPeriod(periodId.value);
    advances.value = (data?.advances ?? []) as unknown as AdvanceRow[];
    summaryByUser.value = (data?.summary_by_user ?? []) as unknown as SummaryByUserRow[];
    totalAdvances.value = data?.total_advances ?? 0;

    log('StepCashRecebimentos', 'loadData', 'Data loaded', { advances: advances.value.length });
  } catch (error) {
    console.error('Error loading advances data:', error);
    eventBus.emit('globalMessage.error', {
      message: (error as Error)?.message || t('Erro ao carregar adiantamentos'),
      duration: 5000
    });
  } finally {
    isLoading.value = false;
  }
};

// Open conciliation wizard
const openConciliationWizard = (): void => {
  log('StepCashRecebimentos', 'openConciliationWizard', 'Opening wizard');
  showConciliationWizard.value = true;
};

// Handle wizard completion
const onConciliationCompleted = (result: unknown): void => {
  log('StepCashRecebimentos', 'onConciliationCompleted', 'Wizard completed', result);
  loadData();
  // Notify via eventBus for any external listeners
  eventBus.emit('payroll.advances.updated');
};

// Handle wizard closed
const onWizardClosed = (): void => {
  log('StepCashRecebimentos', 'onWizardClosed', 'Wizard closed');
  loadData();
};

// Lifecycle - named handler refs for type-safe eventBus.off()
const onAdvancesRefresh = (): void => { loadData(); };
const onAdvancesUpdated = (): void => { loadData(); };

onMounted(() => {
  loadData();

  // Listen for external refresh events
  eventBus.on('payroll.advances.refresh', onAdvancesRefresh);
  eventBus.on('payroll.advances.updated', onAdvancesUpdated);
});

onBeforeUnmount(() => {
  eventBus.off('payroll.advances.refresh', onAdvancesRefresh);
  eventBus.off('payroll.advances.updated', onAdvancesUpdated);
});

// Watch for period changes
watch(() => periodId.value, (newVal) => {
  if (newVal) {
    loadData();
  }
});
</script>

<style scoped>
/* Step Header */
.step-header h2 {
  margin: 0 0 1rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
}

/* Helper Banner */
.helper-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--blue-50);
  border-left: 4px solid var(--blue-500);
  border-radius: 6px;
  margin-bottom: 1rem;
}

.helper-banner i {
  color: var(--blue-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.helper-content strong {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--blue-700);
}

.helper-content ul {
  margin: 0;
  padding-left: 1.25rem;
}

.helper-content li {
  margin-bottom: 0.25rem;
  color: var(--blue-900);
  font-size: 0.9rem;
}

/* Warning Banner */
.warning-banner {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: var(--orange-50);
  border-left: 4px solid var(--orange-500);
  border-radius: 6px;
  margin-bottom: 1rem;
}

.warning-banner i {
  color: var(--orange-500);
  font-size: 1.5rem;
  flex-shrink: 0;
}

.warning-content strong {
  display: block;
  margin-bottom: 0.25rem;
  color: var(--orange-700);
}

.warning-content p {
  margin: 0;
  color: var(--orange-900);
  font-size: 0.9rem;
  line-height: 1.5;
}

/* Important Notes */
.important-notes {
  padding: 1.25rem;
  background: var(--surface-0);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  margin-bottom: 1rem;
}

.notes-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--surface-border);
}

.notes-header i {
  font-size: 1.25rem;
  color: var(--primary-color);
}

.notes-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.notes-list {
  margin: 0;
  padding-left: 0;
  list-style: none;
}

.notes-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  color: var(--text-color);
  line-height: 1.5;
}

.notes-list li i {
  color: var(--primary-color);
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.step-panel {
  padding: 1.5rem;
}

.step-description {
  color: var(--text-color-secondary);
  margin-bottom: 1rem;
}

.info-message {
  margin-bottom: 1.5rem;
}

.reconfirm-warning-message {
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
}

.warning-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.warning-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--yellow-900);
}

.warning-text {
  margin: 0;
  color: var(--yellow-800);
  line-height: 1.5;
}

.warning-highlight {
  padding: 0.75rem;
  background: var(--yellow-100);
  border-left: 3px solid var(--yellow-500);
  border-radius: 4px;
  color: var(--yellow-900);
  line-height: 1.6;
}

.warning-highlight strong {
  color: var(--yellow-900);
  font-weight: 700;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
}

.advances-container {
  margin-top: 1.5rem;
}

/* Conciliation Card */
.conciliation-card {
  background: var(--surface-card);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
  margin-bottom: 1.5rem;
}

.card-body {
  padding: 1.5rem;
}

/* Summary Grid */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.summary-item .item-label {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  font-weight: 500;
}

.summary-item .item-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-color);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.summary-item.highlight .item-value {
  color: var(--orange-600);
  font-size: 1.5rem;
}

/* Status Indicator */
.status-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.status-indicator.pending {
  background-color: var(--surface-400);
}

.status-indicator.in-progress {
  background-color: var(--yellow-500);
  animation: pulse 1.5s infinite;
}

.status-indicator.completed {
  background-color: var(--green-500);
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Action Section */
.action-section {
  margin-bottom: 1.5rem;
}

.action-button {
  width: 100%;
  max-width: 300px;
}

/* Info Messages Inline */
.info-message-inline {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
}

.info-message-inline i {
  margin-top: 2px;
  flex-shrink: 0;
}

.info-message-inline.pending {
  background: var(--surface-50);
  color: var(--text-color-secondary);
}

.info-message-inline.pending i {
  color: var(--surface-500);
}

.info-message-inline.in-progress {
  background: var(--yellow-50);
  color: var(--yellow-800);
}

.info-message-inline.in-progress i {
  color: var(--yellow-500);
}

.info-message-inline.completed {
  background: var(--green-50);
  color: var(--green-800);
}

.info-message-inline.completed i {
  color: var(--green-500);
}

/* Responsive */
@media (max-width: 768px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }

  .action-button {
    max-width: 100%;
  }
}
</style>
